import React from 'react';
import classNames from 'classnames';

import { useIntl, FormattedMessage } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';
import { languagesHelper as language } from '../../../util/languages';

import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, listingsByFilters, useTranslations, intl } = props;

  const filterCounts = listingsByFilters?.[name];

  // Counts not loaded yet — don't show "no options" or crash on Object.values(undefined).
  if (!filterCounts) {
    return null;
  }

  const countListingsTotal = Object.values(filterCounts).reduce(
    (acc, current) => acc + (Number(current) || 0),
    0
  );

  if (countListingsTotal === 0) {
    return (
      <div className={className}>
        <p className={css.emptyText}>
          <FormattedMessage id="SelectMultipleFilter.emptyText" />
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <ul className={css.list}>
        {options.map(({ option, label }) => {
          const fieldId = `${id}.${option}`;
          const countListings = filterCounts[option] ?? 0;
          const optionLabel = useTranslations ? language.labelsTranslator(option, intl) : label;

          if (countListings === 0) return null;

          return (
            <li key={fieldId} className={css.item}>
              <span className={css.checkbox}>
                <FieldCheckbox
                  id={fieldId}
                  name={name}
                  label={optionLabel}
                  value={option}
                  countListings={countListings}
                />
              </span>
              {/* <span className={css.countListingsValue}>• {countListings}</span> */}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {Array<Object>} props.options - The options
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType - The schema type
 * @param {'has_all' | 'has_any'} props.searchMode - The search mode
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const SelectMultipleFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    listingsByFilters,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  const selectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  const labelTranslated = language.labelsTranslator(`pub_${name}`, intl);

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
      { id: 'SelectMultipleFilter.labelSelected' },
      { labelText: labelTranslated, count: selectedOptions.length }
    )
    : labelTranslated;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
      { id: 'SelectMultipleFilterPlainForm.labelSelected' },
      { count: selectedOptions.length }
    )
    : '';

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={labelForPopup}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        listingsByFilters={listingsByFilters}
        intl={intl}
        useTranslations
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={labelTranslated}
      labelSelection={labelSelectionForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        listingsByFilters={listingsByFilters}
        intl={intl}
        useTranslations
      />
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
